import { nextShift, type LifeState } from './life.ts';
import { datesAhead } from './dating.ts';
import { gigsAhead } from './opportunities.ts';
import { eventsBooked, EVENT_LENGTH } from './events.ts';
import { meetupsAhead } from './meetups.ts';
import { dayOf } from './life.ts';

/**
 * What you've said you'll be at: today's shift or class, the dates you've booked, the gigs you've booked. Each with
 * when it starts and the last moment you can still turn up (after that it counts as missed). Long things at home
 * (sleeping, a film, cooking) check these first: "this could make you miss…".
 */
export interface Commitment { what: string; at: number; until: number; kind: 'work' | 'date' | 'gig' | 'event' | 'meetup' }

const DAY = 1440;

export function commitments(s: LifeState): Commitment[] {
  const out: Commitment[] = [];
  const ns = nextShift(s);
  if (ns) out.push({ what: s.job ? `your shift at ${s.job.place}` : 'class', at: ns.start, until: ns.lastChance, kind: 'work' });
  for (const d of datesAhead(s)) out.push({ what: `your date with ${d.name.split(' ')[0]}${d.venue ? ` (${d.venue.split(',')[0]})` : ''}`, at: d.at, until: d.at + 90, kind: 'date' });
  for (const g of gigsAhead(s)) out.push({ what: `the gig: ${g.title}`, at: g.date * DAY + 8 * 60, until: g.date * DAY + 16 * 60, kind: 'gig' });
  for (const e of eventsBooked(s)) out.push({ what: `the sip and paint at ${e.venue.split(',')[0]}`, at: e.at, until: e.at + EVENT_LENGTH - 60, kind: 'event' });
  const w = s.engaged?.wedding;
  if (w) out.push({ what: `your wedding with ${s.engaged!.name.split(' ')[0]}`, at: dayOf(w.date) * DAY + 10 * 60, until: dayOf(w.date) * DAY + 20 * 60, kind: 'event' });
  for (const m of meetupsAhead(s)) out.push({ what: `meeting ${m.name.split(' ')[0]} (${m.place.split(',')[0]})`, at: m.at, until: m.at + 90, kind: 'meetup' });
  return out.filter((c) => c.until > s.minutes).sort((a, b) => a.at - b.at);
}

/** What you'd be late for, or miss, if you spent the next `minutes` on something else. */
export function wouldMiss(s: LifeState, minutes: number): (Commitment & { miss: boolean })[] {
  const end = s.minutes + minutes;
  return commitments(s).filter((c) => c.at <= end).map((c) => ({ ...c, miss: end > c.until }));
}
